import CircleWarningIcon from '@assets/icons/circle-warning.svg';
import i18n from '@/utils/i18n';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  aggregateEditDeltasByCustomer,
  applyEditDeltasToCustomerRows,
  computeCustomerListSummaryTotals,
} from '@sales-portal/domain/pricing/screens/customers/blocs/utils/customer-list-aggregations';
import { applyPricingFilters } from '@sales-portal/domain/pricing/blocs/utils/pricing-filters';
import { AppPageLayout } from '@experiences/templates/app-page-layout';
import { buildCustomerFilterSections } from '@sales-portal/domain/pricing/screens/customers/blocs/utils/pricing-customer-filter-sections';
import { Button } from '@usepepper/sprout-kit-ui';
import {
  COLUMN_WIDTH,
  PRICING_CONTENT_MIN_WIDTH,
  PRICING_FILTER_PANEL_WIDTH,
  PRICING_TABLE_LAYOUT,
} from '@sales-portal/domain/pricing/constants';
import {
  ColumnDef,
  createColumnHelper,
  flexRender,
  OnChangeFn,
  SortingState,
} from '@tanstack/react-table';
import { ColumnId } from '@/experiences/sales-portal/domain/pricing/screens/customers/types';
import {
  ConfigurationDomain,
  TableName,
} from '@/domain/configuration/blocs/stores/column-preferences-keys';
import { ConfigureColumnsButton } from '@experiences/components/table/configure-columns-button';
import { createStyles } from '@/contexts/theme-context';
import {
  customerListGlobalFilterFn,
  isLeftAlignedColumn,
} from '@sales-portal/domain/pricing/screens/customers/blocs/utils/table-ui';
import { EmptyState } from '@experiences/components/empty-state';
import {
  formatCurrency,
  getCurrencySymbol,
  MICRO_CONVERSION,
} from '@/utils/money';
import { formatDate, momentWithTimezone } from '@/utils/date';
import { formatPercentage } from '@sales-portal/blocs/utils/money';
import { LayoutChangeEvent, View } from 'react-native';
import {
  PricingAnalyticEvent,
  pricingManagementAnalytics,
} from '@sales-portal/domain/pricing/analytics/pricing-management-analytics';
import { PricingBreadcrumbs } from '@sales-portal/domain/pricing/components/pricing-breadcrumbs';
import {
  PricingCustomerListRow,
  useCustomerListRowTransformer,
} from '@sales-portal/domain/pricing/screens/customers/blocs/transformers/use-customer-list-transformer';
import { PricingCustomersSummaryHeader } from '@/experiences/sales-portal/domain/pricing/screens/customers/components/pricing-customers-summary-header';
import { PricingCustomersTableEmptyComponent } from '@/experiences/sales-portal/domain/pricing/screens/customers/components/pricing-customers-table-empty-component';
import { PricingCustomersTableRow } from '@/experiences/sales-portal/domain/pricing/screens/customers/components/pricing-customers-table-row';
import { PricingEditsBadge } from '@sales-portal/domain/pricing/components/pricing-edits-badge';
import { PricingFilterButton } from '@sales-portal/domain/pricing/components/pricing-filter-button';
import { PricingFilterEmptyState } from '@sales-portal/domain/pricing/components/pricing-filter-empty-state';
import { PricingFilterPanel } from '@sales-portal/domain/pricing/components/pricing-filter-panel';
import {
  PricingFilterScope,
  PricingFilterUnit,
  PricingRangeColumnConfig,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';
import { PricingReviewButton } from '@sales-portal/domain/pricing/screens/management/components/pricing-review-button';
import { PricingSearchBar } from '@sales-portal/domain/pricing/components/pricing-search-bar';
import { PricingTableHeaderLabel } from '@sales-portal/domain/pricing/screens/management/components/pricing-table-header-label';
import { PricingTableLoadingSkeleton } from '@sales-portal/domain/pricing/components/pricing-table-loading-skeleton';
import {
  RenderHeaderRowCallback,
  RenderRowCallback,
} from '@experiences/components/table/types';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesHubShellTemplate } from '@/experiences/sales-portal/templates/sales-hub-shell-template';
import {
  SalesPortalKeyboardScope,
  SalesPortalShortcuts,
} from '@/experiences/sales-portal/platform/shortcuts/sales-portal-shortcuts';
import { SalesPortalStackParams } from '@/navigators/types';
import {
  SearchInput,
  SearchInputRef,
} from '@/experiences/sales-portal/components/search-input';
import { StackScreenProps } from '@react-navigation/stack';
import { Table } from '@experiences/components/table/table';
import { TableHeaderCell } from '@experiences/components/table/table-header-cell';
import { TableHeaderRow } from '@experiences/components/table/table-header-row';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useConfigureColumns } from '@experiences/components/table/blocs/hooks/use-configure-columns';
import { useCustomerListDataLoader } from '@sales-portal/domain/pricing/screens/customers/blocs/loaders/customer-list-data-loader';
import { useFocusedPricingEditsStore } from '@sales-portal/domain/pricing/blocs/hooks/use-focused-pricing-edits-store';
import { usePricingEditsTimezoneSync } from '@sales-portal/domain/pricing/screens/management/blocs/hooks/use-pricing-edits-timezone-sync';
import { usePricingFilterPanel } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filter-panel';
import { usePricingFilters } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filters';
import { usePricingFiltersUrlSync } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filters-url-sync';
import { usePricingSearch } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-search';
import { usePricingTableLoadingState } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-table-loading-state';
import { useScreenScopedKeyboardShortcuts } from '@experiences/platform/shortcuts/use-screen-scoped-keyboard-shortcuts';

const columnHelper = createColumnHelper<PricingCustomerListRow>();

const NUMERIC_COLUMN_PERCENTAGE =
  (100 - PRICING_TABLE_LAYOUT.NAME_COLUMN_PERCENTAGE) /
  PRICING_TABLE_LAYOUT.CUSTOMERS_COLUMN_COUNT;

type Props = StackScreenProps<
  SalesPortalStackParams,
  typeof SALES_PORTAL_ROUTES.PricingCustomers
>;

export const PricingCustomersScreen: React.FC<Props> = (props) => {
  const { navigation, route } = props;
  const [styles, theme] = useStyles();
  const [tableLayoutWidth, setTableLayoutWidth] = useState<number | null>(null);
  const handleTableLayout = useCallback(
    (event: LayoutChangeEvent) =>
      setTableLayoutWidth(event.nativeEvent.layout.width),
    [],
  );
  const { clearSearch, debouncedSearchText, searchText, setSearchText } =
    usePricingSearch();
  const [sorting, setSorting] = useState<SortingState>([
    { desc: false, id: ColumnId.Name },
  ]);
  const edits = useFocusedPricingEditsStore((state) => state.edits);

  const restaurantEditCounts = useMemo(
    () =>
      edits.reduce<Record<string, number>>((acc, edit) => {
        acc[edit.restaurantUUID] = (acc[edit.restaurantUUID] ?? 0) + 1;
        return acc;
      }, {}),
    [edits],
  );

  const { currencyCode, timezoneLoading } = usePricingEditsTimezoneSync();

  const { data, loading, error, refetch } = useCustomerListDataLoader();

  const { alertMe, loadingState, onAlertMePress } = usePricingTableLoadingState(
    {
      error,
      loading: loading || timezoneLoading,
    },
  );

  const hasTrackedLoad = useRef(false);
  useEffect(() => {
    if (!loading && !error && data && !hasTrackedLoad.current) {
      hasTrackedLoad.current = true;
      pricingManagementAnalytics.track(
        PricingAnalyticEvent.LOAD_COST_CHANGES_CUSTOMER_LIST,
        {},
      );
    }
  }, [loading, error, data]);

  const rows = useCustomerListRowTransformer(
    data?.getCustomerListDataForBusinessOrganization.customers ?? [],
  );

  const editDeltasByCustomer = useMemo(
    () => aggregateEditDeltasByCustomer(edits),
    [edits],
  );

  const mergedRows = useMemo(
    () =>
      edits.length === 0
        ? rows
        : applyEditDeltasToCustomerRows(rows, editDeltasByCustomer),
    [edits.length, editDeltasByCustomer, rows],
  );

  const onSortingChange = useCallback<OnChangeFn<SortingState>>(
    (next) => setSorting(next),
    [],
  );

  const handleSearchChange = (text: string) => {
    setSearchText(text);
  };

  const searchInputRef = useRef<SearchInputRef>(null);

  // Keyboard shortcut: / to focus search input
  useScreenScopedKeyboardShortcuts(
    SalesPortalShortcuts.Search,
    () => searchInputRef.current?.focus(),
    [],
    {},
    true,
    SalesPortalKeyboardScope.PRICING_MANAGEMENT,
  );

  const columns = useMemo<ColumnDef<PricingCustomerListRow, string>[]>(
    () => [
      // Customer
      columnHelper.accessor((row) => row.restaurantName, {
        cell: (info) => {
          const editCount =
            restaurantEditCounts[info.row.original.restaurantUUID];
          return (
            <View style={styles.nameCellContainer}>
              <Typography variant="paragraph-md-600">
                {info.getValue()}
              </Typography>
              {editCount ? <PricingEditsBadge editsCount={editCount} /> : null}
            </View>
          );
        },
        header: () => i18n.t('pricing.table.header.customer'),
        id: ColumnId.Name,
        meta: {
          alwaysVisible: true,
          initialPercentWidth: PRICING_TABLE_LAYOUT.NAME_COLUMN_PERCENTAGE,
          label: i18n.t('pricing.table.header.customer'),
        },
        minSize: PRICING_TABLE_LAYOUT.NAME_COLUMN_MIN_WIDTH,
        sortingFn: 'alphanumeric',
      }),
      // ID
      columnHelper.accessor((row) => row.restaurantAccountId, {
        cell: (info) => (
          <Typography
            color={theme.sk.color.text.primary}
            variant="paragraph-md"
          >
            {info.getValue() || '--'}
          </Typography>
        ),
        header: () => i18n.t('pricing.table.header.id'),
        id: ColumnId.AccountId,
        meta: {
          initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
          label: i18n.t('pricing.table.header.id'),
        },
        ...COLUMN_WIDTH.M,
        sortingFn: 'alphanumeric',
      }),
      // Sales Rep
      columnHelper.accessor((row) => row.salesRepName, {
        cell: (info) => (
          <Typography
            color={theme.sk.color.text.primary}
            variant="paragraph-md"
          >
            {info.getValue() ?? '--'}
          </Typography>
        ),
        header: () => i18n.t('pricing.table.header.sales_rep'),
        id: ColumnId.SalesRep,
        meta: {
          initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
          label: i18n.t('pricing.table.header.sales_rep'),
        },
        ...COLUMN_WIDTH.M,
        sortUndefined: 'last',
      }),
      // Last Invoice
      columnHelper.accessor(
        (row) =>
          row.lastInvoiceDate
            ? formatDate(momentWithTimezone(row.lastInvoiceDate))
            : '--',
        {
          cell: (info) => (
            <Typography variant="paragraph-md">{info.getValue()}</Typography>
          ),
          header: () => i18n.t('pricing.table.header.last_invoice'),
          id: ColumnId.LastInvoiceDate,
          meta: {
            initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
            label: i18n.t('pricing.table.header.last_invoice'),
          },
          ...COLUMN_WIDTH.S,
          sortUndefined: 'last',
        },
      ),
      // Avg Rev/wk
      columnHelper.accessor(
        (row) => formatCurrency(row.revenueInMicros, currencyCode),
        {
          cell: (info) => (
            <Typography variant="paragraph-md">{info.getValue()}</Typography>
          ),
          header: () => (
            <PricingTableHeaderLabel
              label={i18n.t('pricing.table.header.weekly_revenue')}
              tooltipText={i18n.t(
                'pricing.table.header.weekly_revenue.tooltip_text',
              )}
            />
          ),
          id: ColumnId.Revenue,
          meta: {
            initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
            label: i18n.t('pricing.table.header.weekly_revenue'),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) =>
            rowA.original.revenueInMicros - rowB.original.revenueInMicros,
        },
      ),
      // Avg GP/wk
      columnHelper.accessor(
        (row) => formatCurrency(row.grossProfitInMicros, currencyCode),
        {
          cell: (info) => (
            <Typography variant="paragraph-md">{info.getValue()}</Typography>
          ),
          header: () => (
            <PricingTableHeaderLabel
              label={i18n.t('pricing.table.header.weekly_gross_profit')}
              tooltipText={i18n.t(
                'pricing.table.header.weekly_gross_profit.tooltip_text',
              )}
            />
          ),
          id: ColumnId.GrossProfit,
          meta: {
            initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
            label: i18n.t('pricing.table.header.weekly_gross_profit'),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) =>
            rowA.original.grossProfitInMicros -
            rowB.original.grossProfitInMicros,
        },
      ),
      // Realized GM%
      columnHelper.accessor(
        (row) =>
          row.marginPercentage === null
            ? '--'
            : formatPercentage(row.marginPercentage, 1, 1),
        {
          cell: (info) => (
            <Typography variant="paragraph-md">{info.getValue()}</Typography>
          ),
          header: () => (
            <PricingTableHeaderLabel
              label={i18n.t('pricing.table.header.realized_gross_margin')}
              tooltipText={i18n.t(
                'pricing.table.header.realized_gross_margin.tooltip_text',
              )}
            />
          ),
          id: ColumnId.RealizedGrossMargin,
          meta: {
            initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
            label: i18n.t('pricing.table.header.realized_gross_margin'),
          },
          ...COLUMN_WIDTH.S,
          sortUndefined: 'last',
        },
      ),
      // Avg Vol/wk
      columnHelper.accessor((row) => row.lineItems.toFixed(1), {
        cell: (info) => (
          <Typography variant="paragraph-md">{info.getValue()}</Typography>
        ),
        header: () => i18n.t('pricing.table.header.weekly_volume'),
        id: ColumnId.LineItems,
        meta: {
          initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
          label: i18n.t('pricing.table.header.weekly_volume'),
        },
        ...COLUMN_WIDTH.XS,
        sortingFn: (rowA, rowB) =>
          rowA.original.lineItems - rowB.original.lineItems,
      }),
      // Weekly edits impact
      columnHelper.accessor(
        (row) =>
          formatCurrency(
            editDeltasByCustomer[row.restaurantUUID]
              ?.grossProfitImpactInMicros ?? 0,
            currencyCode,
          ),
        {
          cell: (info) => (
            <Typography variant="paragraph-md">{info.getValue()}</Typography>
          ),
          header: () => (
            <PricingTableHeaderLabel
              label={i18n.t('pricing.table.header.weekly_edits_impact')}
              tooltipText={i18n.t(
                'pricing.table.header.weekly_edits_impact.tooltip_text',
              )}
            />
          ),
          id: ColumnId.WeeklyEditsGrossProfitImpact,
          meta: {
            initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
            label: i18n.t('pricing.table.header.weekly_edits_impact'),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) =>
            (editDeltasByCustomer[rowA.original.restaurantUUID]
              ?.grossProfitImpactInMicros ?? 0) -
            (editDeltasByCustomer[rowB.original.restaurantUUID]
              ?.grossProfitImpactInMicros ?? 0),
        },
      ),
    ],
    [
      currencyCode,
      editDeltasByCustomer,
      restaurantEditCounts,
      styles.nameCellContainer,
      theme.sk.color.text.primary,
    ],
  );

  const { columnOrder, columnVisibility } = useConfigureColumns(
    ConfigurationDomain.Pricing,
    TableName.PricingCustomerList,
    columns,
  );

  const rangeColumnConfigs = useMemo<
    Record<string, PricingRangeColumnConfig<PricingCustomerListRow>>
  >(() => {
    const currencyAffix = getCurrencySymbol(currencyCode);

    return {
      [ColumnId.GrossProfit]: {
        accessor: (row) => row.grossProfitInMicros / MICRO_CONVERSION,
        affix: currencyAffix,
        title: i18n.t('pricing.table.header.weekly_gross_profit'),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.LineItems]: {
        accessor: (row) => row.lineItems,
        affix: 'u',
        title: i18n.t('pricing.table.header.weekly_volume'),
        unit: PricingFilterUnit.Unit,
      },
      [ColumnId.RealizedGrossMargin]: {
        accessor: (row) => row.marginPercentage,
        affix: '%',
        title: i18n.t('pricing.table.header.realized_gross_margin'),
        unit: PricingFilterUnit.Percent,
      },
      [ColumnId.Revenue]: {
        accessor: (row) => row.revenueInMicros / MICRO_CONVERSION,
        affix: currencyAffix,
        title: i18n.t('pricing.table.header.weekly_revenue'),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.WeeklyEditsGrossProfitImpact]: {
        accessor: (row) =>
          (editDeltasByCustomer[row.restaurantUUID]
            ?.grossProfitImpactInMicros ?? 0) / MICRO_CONVERSION,
        affix: currencyAffix,
        title: i18n.t('pricing.table.header.weekly_edits_impact'),
        unit: PricingFilterUnit.Currency,
      },
    };
  }, [currencyCode, editDeltasByCustomer]);

  // Declared order, never `columnOrder` — rearranging the table must not move
  // the filters.
  const filterColumnIds = useMemo(
    () =>
      columns
        .map((column) => column.id)
        .filter((id): id is string => Boolean(id)),
    [columns],
  );

  const sections = useMemo(
    () =>
      buildCustomerFilterSections({
        columnIds: filterColumnIds,
        editCounts: restaurantEditCounts,
        rangeColumnConfigs,
        rows: mergedRows,
      }),
    [filterColumnIds, restaurantEditCounts, rangeColumnConfigs, mergedRows],
  );

  const filters = usePricingFilters(PricingFilterScope.Customers, sections);
  const panel = usePricingFilterPanel();

  const handleFiltersUrlUpdate = useCallback(
    (params: Record<string, string | undefined>) =>
      navigation.setParams(params),
    [navigation],
  );

  usePricingFiltersUrlSync({
    hydrate: filters.hydrate,
    onUrlUpdate: handleFiltersUrlUpdate,
    routeParams: route.params ?? {},
    sections,
    selections: { pills: filters.pills, ranges: filters.ranges },
  });

  // Drop filters whose column is gone, so they can't silently keep shrinking
  // the table. Depends on the store action, not `filters`, which would loop.
  useEffect(() => {
    filters.pruneSections(sections.map((section) => section.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, filters.pruneSections]);

  // Store slices, not `filters`: that object is new every render, so the
  // Table would get a new array on every keystroke.
  const { pills: filterPills, ranges: filterRanges } = filters;
  const transformedData = useMemo(
    () =>
      applyPricingFilters(
        mergedRows,
        { pills: filterPills, ranges: filterRanges },
        sections,
      ),
    [mergedRows, filterPills, filterRanges, sections],
  );

  const summaryTotals = useMemo(
    () =>
      computeCustomerListSummaryTotals(transformedData, editDeltasByCustomer),
    [transformedData, editDeltasByCustomer],
  );

  const handleClearEmptyState = useCallback(() => {
    if (debouncedSearchText.length > 0) {
      clearSearch();
    } else {
      filters.clearAll();
    }
  }, [debouncedSearchText, clearSearch, filters]);

  const renderHeaderRow = useCallback<
    RenderHeaderRowCallback<PricingCustomerListRow>
  >(
    ({ headerGroup }) => (
      <TableHeaderRow key={headerGroup.id}>
        {headerGroup.headers.map((header) => (
          <TableHeaderCell
            columnId={header.id}
            key={header.id}
            onPress={
              header.column.getCanSort()
                ? () => header.column.toggleSorting()
                : undefined
            }
            pinnedOffset={
              header.column.getIsPinned()
                ? header.column.getStart('left')
                : undefined
            }
            sortDirection={header.column.getIsSorted() || undefined}
            style={
              isLeftAlignedColumn(header.column.id)
                ? undefined
                : styles.headerCell
            }
          >
            {header.isPlaceholder ? null : (
              <View style={styles.columnHeaderContainer}>
                <Typography
                  color={theme.sk.color.text.tertiary}
                  textAlign={
                    isLeftAlignedColumn(header.column.id) ? undefined : 'right'
                  }
                  variant="paragraph-sm-600"
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </Typography>
              </View>
            )}
          </TableHeaderCell>
        ))}
      </TableHeaderRow>
    ),
    [styles, theme],
  );

  const renderRow = useCallback<RenderRowCallback<PricingCustomerListRow>>(
    ({ index, row }) => (
      <PricingCustomersTableRow
        columnOrder={columnOrder}
        columnVisibility={columnVisibility}
        index={index}
        key={row.id}
        row={row}
      />
    ),
    [columnOrder, columnVisibility],
  );

  const loadingSkeleton = (
    <PricingTableLoadingSkeleton
      alertMe={alertMe}
      loadingState={loadingState}
      onAlertMePress={onAlertMePress}
      phraseKeyPrefix="pricing.customers.table.loading"
      testID="pricing-customers-loading-skeleton"
    />
  );

  return (
    <SalesHubShellTemplate>
      <AppPageLayout
        actions={
          edits.length > 0 ? (
            <View
              style={panel.isOpen ? styles.actionsWithPanelOpen : undefined}
            >
              <PricingReviewButton
                onPress={() => {
                  navigation.navigate(
                    SALES_PORTAL_ROUTES.PricingManagementReview,
                  );
                }}
              />
            </View>
          ) : undefined
        }
        leadingActions={
          <PricingBreadcrumbs
            secondaryBreadcrumb={i18n.t('pricing.customers.sidenav_title')}
          />
        }
        overlay={
          panel.isOpen ? (
            <PricingFilterPanel
              filters={filters}
              onClose={panel.close}
              sections={sections}
            />
          ) : null
        }
        scroll={false}
      >
        <View
          style={[
            styles.container,
            panel.isOpen && styles.containerWithPanelOpen,
          ]}
        >
          {loading || timezoneLoading ? (
            loadingSkeleton
          ) : error ? (
            <EmptyState
              bottomElement={
                <Button
                  accessibilityLabel={i18n.t('sales_portal.error.retry')}
                  onPress={async () => await refetch()}
                  style={styles.autoAlignSelf}
                  variant="secondary"
                >
                  {i18n.t('sales_portal.error.retry')}
                </Button>
              }
              description={i18n.t('pricing.error.table.description')}
              iconElement={
                <CircleWarningIcon stroke={theme.sk.color.icon.primary} />
              }
              title={i18n.t('pricing.error.table.title')}
            />
          ) : (
            <Suspense fallback={loadingSkeleton}>
              <PricingSearchBar
                trailing={
                  <>
                    <PricingFilterButton
                      activeCount={filters.activeCount}
                      onPress={panel.open}
                    />
                    <ConfigureColumnsButton
                      columns={columns}
                      domain={ConfigurationDomain.Pricing}
                      table={TableName.PricingCustomerList}
                    />
                  </>
                }
              >
                <SearchInput
                  onSearchChange={handleSearchChange}
                  placeholderText={i18n.t('pricing.search_placeholder')}
                  ref={searchInputRef}
                  searchText={searchText}
                />
              </PricingSearchBar>
              <PricingCustomersSummaryHeader
                avgMarginPercentage={summaryTotals.avgMarginPercentage}
                currencyCode={currencyCode}
                customerCount={summaryTotals.customerCount}
                totalEditsImpactInMicros={
                  summaryTotals.totalEditsImpactInMicros
                }
                totalGrossProfitInMicros={
                  summaryTotals.totalGrossProfitInMicros
                }
                totalRevenueInMicros={summaryTotals.totalRevenueInMicros}
              />
              <View onLayout={handleTableLayout} style={styles.tableWrapper}>
                <Table
                  cellPadding={[
                    ...Array(columns.length).fill(theme.sk.spacing.spaceSmall),
                  ]}
                  columns={columns}
                  data={transformedData}
                  emptyComponent={
                    <PricingFilterEmptyState
                      baseEmptyComponent={
                        <PricingCustomersTableEmptyComponent />
                      }
                      hasActiveFilters={filters.hasActiveFilters}
                      onClear={handleClearEmptyState}
                      searchText={debouncedSearchText}
                    />
                  }
                  estimatedRowHeight={PRICING_TABLE_LAYOUT.ESTIMATED_ROW_HEIGHT}
                  fillAvailableWidth
                  globalFilterFn={customerListGlobalFilterFn}
                  headerPadding={theme.sk.spacing.spaceSmall}
                  initialState={{
                    columnPinning: { left: [ColumnId.Name] },
                  }}
                  isActive
                  layoutWidth={tableLayoutWidth ?? undefined}
                  onSortingChange={onSortingChange}
                  renderHeaderRow={renderHeaderRow}
                  renderRow={renderRow}
                  state={{
                    columnOrder,
                    columnVisibility,
                    globalFilter: debouncedSearchText,
                    sorting,
                  }}
                />
              </View>
            </Suspense>
          )}
        </View>
      </AppPageLayout>
    </SalesHubShellTemplate>
  );
};

const useStyles = createStyles((theme) => ({
  actionsWithPanelOpen: {
    paddingRight: PRICING_FILTER_PANEL_WIDTH,
  },
  autoAlignSelf: {
    alignSelf: 'auto',
  },
  columnHeaderContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    flexShrink: 1,
    minWidth: 0,
  },
  container: {
    flex: 1,
    gap: theme.sk.spacing.spaceMedium,
    paddingTop: theme.sk.spacing.spaceMedium,
  },
  containerWithPanelOpen: {
    minWidth: PRICING_CONTENT_MIN_WIDTH + PRICING_FILTER_PANEL_WIDTH,
    paddingRight: PRICING_FILTER_PANEL_WIDTH,
  },
  headerCell: {
    alignItems: 'flex-end',
  },
  nameCellContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    justifyContent: 'space-between',
  },
  tableWrapper: {
    flex: 1,
    position: 'relative',
  },
}));
